import { useEffect, useState } from "react";
import { fileUrl, printAgent } from "@/lib/api";
import { Printer, Download, RefreshCw, Apple, Monitor, CheckCircle2, XCircle, KeyRound, Receipt, FileText, Tag } from "lucide-react";
import { SettingsCard, SectionLabel } from "./ui";
import { Button, Field, Input } from "@/components/ui-kit";
import { toast } from "sonner";

// Servidos pelo backend a partir de storage/app/private/print-agent/ — se o
// instalador não estiver publicado, a rota responde 404 (e não o HTML da SPA).
//
// `fileUrl` e não a URL crua: a rota deixou de ser pública (qualquer pessoa com
// o endereço baixava os 100 MB) e agora pede login de quem opera o balcão. O
// token vai na query porque isto é um `<a download>`, que não carrega cabeçalho.
// Função, e não constante: `fileUrl` lê o token na hora da chamada, e uma
// constante de módulo o congelaria no import — a tela abriria com o token de
// antes do login, ou com `null`.
const downloadDoAgente = (plataforma) => fileUrl(`/print-agent/download/${plataforma}`);

/**
 * Os três papéis, e o que deixa de sair quando cada um está sem impressora.
 *
 * A configuração em si mora no app da bandeja — é lá que estão os nomes das
 * impressoras daquela máquina. O que esta tela faz é DIAGNÓSTICO: mostrar ao
 * administrador, sem levantar do lugar, que o balcão está sem etiquetadora.
 * Sem isso, a falta só aparece no momento em que alguém precisa imprimir.
 */
const PAPEIS = [
  { id: "thermal", titulo: "Bobina térmica", icon: Receipt,  usa: "Cupom de venda, NFC-e e comprovante de caixa" },
  { id: "a4",      titulo: "A4 / laser",     icon: FileText, usa: "DANFE de NF-e, orçamento, contrato e relatório" },
  { id: "label",   titulo: "Etiqueta",       icon: Tag,      usa: "Código de barras e preço de produto" },
];

export default function PrintAgentTab() {
  const [state, setState] = useState({ loading: true, online: false, printers: [], requiresToken: false, profiles: {} });
  // O agente lê este token de `osp_print_token`, mas NENHUMA tela jamais o
  // gravava: quem preenchesse o campo no app da bandeja derrubava a impressão
  // da loja e via "agente conectado" em verde, porque /health não valida token.
  const [token, setToken] = useState(() => localStorage.getItem("osp_print_token") || "");

  const check = async () => {
    setState((s) => ({ ...s, loading: true }));
    const saude = await printAgent.health();
    const online = !!saude;
    let printers = [];
    let tokenInvalido = false;
    // O agente responde /health mesmo para origem não autorizada, com
    // `originAllowed: false`, EXATAMENTE para esta tela conseguir explicar o
    // problema. Ninguém lia: a tela mostrava "Agente conectado" em verde com
    // zero impressoras, e o sintoma virava idêntico ao de agente fechado.
    let origemBloqueada = saude?.originAllowed === false;
    if (online && !origemBloqueada) {
      try { printers = await printAgent.listPrinters(); }
      catch (e) {
        tokenInvalido = e?.code === "token_invalid";
        origemBloqueada = e?.code === "origin_denied";
      }
    }
    const perfis = saude?.profiles || {};
    setState({
      loading: false, online, printers, tokenInvalido, origemBloqueada,
      requiresToken: !!saude?.requiresToken,
      version: saude?.version || null,
      motivoOrigem: saude?.error || null,
      // `undefined` num agente antigo, que não publica o campo — por isso a tela
      // só avisa quando ele vem explicitamente `false`.
      originsConfigured: saude?.originsConfigured,
      /* Agente anterior aos perfis de papel: o /health dele nem tem o campo.
         É esta a checagem que funciona nas máquinas JÁ instaladas — a versão do
         pacote ficou em 2.0.0 durante toda a evolução (perfis, medição de
         bobina, ZPL) e não distingue uma geração da outra. */
      legado: online && !origemBloqueada && Object.keys(perfis).length === 0,
      profiles: perfis,
    });
  };

  const salvarToken = () => {
    const t = token.trim();
    if (t) localStorage.setItem("osp_print_token", t);
    else localStorage.removeItem("osp_print_token");
    toast.success(t ? "Token salvo neste navegador" : "Token removido");
    check();
  };

  useEffect(() => { check(); /* eslint-disable-next-line */ }, []);

  // Verde só quando a impressão realmente funciona daqui. Token errado e origem
  // bloqueada respondem /health normalmente — tratá-los como "conectado" era o
  // que fazia a tela dar um OK para uma máquina que não imprime nada.
  const saudavel = state.online && !state.tokenInvalido && !state.origemBloqueada && !state.legado;

  return (
    <div className="space-y-6">
      <SettingsCard title="Agente de impressão local" icon={Printer} right={
        <button onClick={check} disabled={state.loading} data-testid="print-agent-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand-600 disabled:opacity-50">
          <RefreshCw size={13} className={state.loading ? "animate-spin" : ""} /> Verificar
        </button>
      }>
        {/* Status ao vivo */}
        <div className={`flex items-center gap-3 rounded-lg px-4 py-3 border ${saudavel ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="print-agent-status">
          {saudavel ? <CheckCircle2 size={20} className="text-emerald-600" /> : <XCircle size={20} className="text-amber-600" />}
          <div>
            <p className={`text-sm font-semibold ${saudavel ? "text-emerald-800" : "text-amber-800"}`}>
              {state.loading ? "Verificando…"
                : state.origemBloqueada ? "Agente encontrado, mas este endereço não está autorizado"
                : state.tokenInvalido ? "Agente conectado, mas o token não confere"
                : state.online ? "Agente conectado"
                : "Agente não encontrado nesta máquina"}
            </p>
            <p className="text-xs text-slate-500">
              {state.origemBloqueada
                ? (state.motivoOrigem || "Inclua o endereço deste sistema em Origens autorizadas, na Configuração avançada do app da bandeja.")
                : state.online
                  ? `${state.printers.length} impressora(s) detectada(s) · ponte em ${printAgent.url.replace("http://", "")}${state.version ? ` · versão ${state.version}` : ""}`
                  : "Instale e abra o app nesta máquina para imprimir cupons, contratos e NF-e direto."}
            </p>
          </div>
        </div>

        {/* Agente de geração anterior: o estrago é silencioso e caro em papel. */}
        {state.legado && (
          <div className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="print-agent-legacy">
            <XCircle size={16} className="text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">Este agente está desatualizado.</p>
              <p className="text-xs mt-1 leading-relaxed">
                Ele não conhece os <b>perfis de papel</b>: manda tudo para a impressora padrão em folha inteira —
                inclusive o cupom, que passa a gastar uma folha A4 por venda, e a etiqueta, que sai fora do picote.
                Baixe o instalador abaixo e reinstale nesta máquina.
              </p>
            </div>
          </div>
        )}

        {/* Agente aberto para qualquer página deste computador. É o padrão (e
            continua sendo, para não derrubar loja já instalada), mas quem
            administra precisa saber que existe a trava. */}
        {saudavel && state.originsConfigured === false && !state.requiresToken && (
          <p className="mt-3 text-xs text-amber-700" data-testid="print-agent-open">
            Este agente aceita qualquer página aberta no computador dele. Para restringir, informe o endereço deste
            sistema em <b>Origens autorizadas</b>, na Configuração avançada do app da bandeja.
          </p>
        )}

        {/* Só aparece quando o agente EXIGE token: sem isso o campo confundiria
            quem não usa essa proteção. */}
        {state.online && state.requiresToken && (
          <div className="mt-4 border border-slate-200 rounded-xl p-4">
            <SectionLabel>Token de impressão</SectionLabel>
            <p className="text-sm text-slate-600 mb-3">
              Este agente exige token. Copie o valor no app da bandeja, em <b>Configuração avançada</b>, e cole aqui —
              o token fica salvo <b>neste navegador</b>, então repita em cada máquina.
            </p>
            {state.tokenInvalido && (
              <p role="alert" className="text-sm text-red-600 mb-3">
                O token atual não confere: o agente recusou a conexão.
              </p>
            )}
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Token" className="flex-1 min-w-[16rem]">
                <Input value={token} onChange={(e) => setToken(e.target.value)}
                  placeholder="cole aqui o token do agente" mono data-testid="print-agent-token" />
              </Field>
              <Button icon={KeyRound} onClick={salvarToken} data-testid="print-agent-token-save">Salvar token</Button>
            </div>
          </div>
        )}

        {/* Papéis: o diagnóstico que evita descobrir a falta na hora da venda. */}
        {saudavel && (
          <div className="mt-4">
            <SectionLabel>Papéis configurados nesta máquina</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="print-agent-profiles">
              {PAPEIS.map(({ id, titulo, icon: Icon, usa }) => {
                const p = state.profiles?.[id];
                const ok = !!p?.configured;
                return (
                  <div key={id} data-testid={`print-agent-profile-${id}`}
                    className={`rounded-xl border px-3 py-2.5 ${ok ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50/60"}`}>
                    <div className="flex items-center gap-2">
                      <Icon size={15} className={ok ? "text-emerald-600" : "text-slate-400"} aria-hidden />
                      <span className="text-sm font-semibold text-slate-900">{titulo}</span>
                    </div>
                    <p className={`text-xs mt-1 font-medium ${ok ? "text-emerald-700" : "text-amber-700"}`}>
                      {ok
                        ? `Pronta${p.width_mm ? ` · ${p.width_mm}mm${p.height_mm ? `×${p.height_mm}mm` : ""}` : ""}`
                        : "Sem impressora"}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-snug">{usa}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2.5">
              Para escolher a impressora de cada papel, abra o app <b>Ortopédica SP Impressão</b> na bandeja
              desta máquina. A configuração é <b>por computador</b> — cada balcão tem as suas.
            </p>
          </div>
        )}

        {state.online && state.printers.length > 0 && (
          <div className="mt-4">
            <SectionLabel>Impressoras detectadas</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {state.printers.map((p) => (
                <span key={p} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1.5">
                  <Printer size={13} className="text-slate-400" /> {p}
                </span>
              ))}
            </div>
          </div>
        )}
      </SettingsCard>

      {/* Download */}
      <SettingsCard title="Baixar o agente" icon={Download}>
        <p className="text-sm text-slate-600 mb-4">Instale em <b>cada computador que tem impressora</b> (balcão, recepção). Depois de instalar, o sistema imprime sozinho.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a href={downloadDoAgente("windows")} download data-testid="download-agent-win"
            className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-3 hover:border-brand-400 hover:bg-brand-50/40 transition">
            <Monitor size={22} className="text-brand-600 shrink-0" />
            <span><span className="block text-sm font-semibold text-slate-900">Windows</span><span className="block text-xs text-slate-500">Instalador .exe</span></span>
          </a>
          <a href={downloadDoAgente("mac")} download data-testid="download-agent-mac"
            className="flex items-center gap-3 border border-slate-200 rounded-lg px-4 py-3 hover:border-brand-400 hover:bg-brand-50/40 transition">
            <Apple size={22} className="text-brand-600 shrink-0" />
            <span><span className="block text-sm font-semibold text-slate-900">macOS</span><span className="block text-xs text-slate-500">Imagem .dmg</span></span>
          </a>
        </div>

        <div className="mt-5">
          <SectionLabel>Como instalar</SectionLabel>
          <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
            <li>Baixe e execute o instalador (Windows instala sozinho; no Mac, arraste para Aplicativos).</li>
            {/* O app é assinado em modo ad-hoc, não notarizado pela Apple, então
                o macOS o bloqueia na primeira abertura.
                O atalho de botão direito → Abrir NÃO serve: a Apple o removeu no
                macOS 15, e o diálogo de hoje só oferece "Mover para o Lixo" e
                "OK". Quem seguir aquela instrução conclui que o app é malware e
                joga fora — daí o passo apontar para Ajustes do Sistema, que é o
                único caminho que existe nas versões atuais. */}
            <li className="text-slate-500">
              <b>Só no Mac, na primeira vez:</b> abra o app; vai aparecer um aviso de que a Apple não
              pôde verificá-lo — clique em <b>OK</b> (nunca em &ldquo;Mover para o Lixo&rdquo;).
              Vá em <b>Ajustes do Sistema → Privacidade e Segurança</b>, role até <b>Segurança</b> e
              clique em <b>Abrir Mesmo Assim</b>. É pedido uma única vez.
            </li>
            <li>Abra o app na bandeja e clique em <b>Buscar impressoras</b>.</li>
            <li>Escolha uma impressora para cada papel — <b>bobina térmica</b>, <b>A4</b> e <b>etiqueta</b>.
              Se a loja tem só uma, pode repetir a mesma nos três.</li>
            <li>Clique em <b>Testar</b> em cada papel e confira o resultado — a folha de teste sai com uma
              régua, para você conferir se a largura configurada bate com a impressora.</li>
            <li>Ligue <b>Iniciar com o computador</b> e pronto: o sistema imprime cada documento no papel certo.</li>
          </ol>
          {/* Atalho para quem tem terminal: apaga a marca de quarentena que todo
              download ganha, e o bloqueio some de vez em vez de exigir a visita
              aos Ajustes a cada reinstalação. */}
          <details className="mt-3 text-sm">
            <summary className="cursor-pointer text-brand-600 hover:text-brand-700">
              No Mac, resolver pelo Terminal (mais rápido)
            </summary>
            <p className="text-slate-600 mt-2">
              Depois de arrastar para Aplicativos, cole isto no Terminal e tecle Enter. O app abre
              normalmente daí em diante, sem passar pelos Ajustes:
            </p>
            <code className="block mt-2 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-xs font-mono overflow-x-auto">
              xattr -dr com.apple.quarantine &quot;/Applications/Ortopédica SP Impressão.app&quot;
            </code>
          </details>
        </div>
      </SettingsCard>
    </div>
  );
}
